import { useState, useRef, useEffect, useCallback } from "react";
import styled from "styled-components";
import { AnimatePresence, motion } from "motion/react";

import useWizard from "../useWizard";
import { ACCENT_COLOR } from "../theme";

/* ─── Rich‑text editor (bold / italic / link) ─── */

function RichEditor({ value, onChange, placeholder, onCancel }) {
  const editorRef = useRef(null);

  useEffect(() => {
    if (editorRef.current) {
      editorRef.current.innerHTML = value || "";
      // Delay focus until after the enter animation
      const t = setTimeout(() => {
        const el = editorRef.current;
        if (!el) return;
        const sel = window.getSelection();
        const range = document.createRange();
        const hasContent = el.textContent.trim().length > 0;
        if (hasContent) {
          // Move cursor to end
          range.selectNodeContents(el);
          range.collapse(false);
        } else {
          // Move cursor to beginning
          range.setStart(el, 0);
          range.collapse(true);
        }
        sel.removeAllRanges();
        sel.addRange(range);
        el.focus();
      }, 350);
      return () => clearTimeout(t);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleInput = useCallback(() => {
    if (editorRef.current) onChange(editorRef.current.innerHTML);
  }, [onChange]);

  const exec = useCallback(
    (cmd, val) => {
      document.execCommand(cmd, false, val);
      editorRef.current?.focus();
      handleInput();
    },
    [handleInput]
  );

  const handleLink = useCallback(
    (e) => {
      e.preventDefault();
      const url = window.prompt("Enter URL:");
      if (url) exec("createLink", url);
    },
    [exec]
  );

  const empty =
    !value || value.replace(/<[^>]*>/g, "").trim().length === 0;

  return (
    <EditorWrap>
      <Editor
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={handleInput}
        $empty={empty}
        data-placeholder={placeholder}
      />
      <Toolbar>
        <CancelBtn onMouseDown={(e) => e.preventDefault()} onClick={onCancel} title="Cancel">
          <CancelBtnInner><i className="fas fa-xmark" /> Remove Release</CancelBtnInner>
        </CancelBtn>
        <ToolbarBtnGroup>
          <ToolbarBtn
            onMouseDown={(e) => {
              e.preventDefault();
              exec("bold");
            }}
            title="Bold"
          >
            <i className="fas fa-bold" />
          </ToolbarBtn>
          <ToolbarBtn
            onMouseDown={(e) => {
              e.preventDefault();
              exec("italic");
            }}
            title="Italic"
          >
            <i className="fas fa-italic" />
          </ToolbarBtn>
          <ToolbarBtn onMouseDown={handleLink} title="Insert link">
            <i className="fas fa-link" />
          </ToolbarBtn>
        </ToolbarBtnGroup>
      </Toolbar>
    </EditorWrap>
  );
}

/* ─── Release pane (choice + inline editor) ─── */

function ReleasePane({ title, subtitle, yesIcon = "fas fa-clipboard-check", yesLabel, noLabel, customField, declinedField, textField, placeholder }) {
  const { data, updateField, setBackOverride, nextSubStep } = useWizard();
  const perm = data.permissionRelease;
  const isDeclined = perm[declinedField] === true;
  const [showDeclinedSelected, setShowDeclinedSelected] = useState(isDeclined);
  const [showEditor, setShowEditor] = useState(() => {
    if (!perm[customField]) return false;
    const text = (perm[textField] || "").replace(/<[^>]*>/g, "").trim();
    return text.length > 0;
  });
  const hasToggled = useRef(false);
  const pendingClear = useRef(false);
  const textRef = useRef(perm[textField]);
  textRef.current = perm[textField];

  useEffect(() => {
    if (showEditor) {
      setBackOverride(() => {
        const text = (textRef.current || "").replace(/<[^>]*>/g, "").trim();
        if (text.length > 0) return false;
        handleBack();
        return true;
      });
    } else {
      setBackOverride(null);
    }
    return () => setBackOverride(null);
  }, [showEditor, setBackOverride]);

  const handleBack = () => {
    hasToggled.current = true;
    updateField("permissionRelease", customField, false);
    setShowEditor(false);
  };

  const handleCancel = () => {
    hasToggled.current = true;
    pendingClear.current = true;
    updateField("permissionRelease", customField, false);
    setShowEditor(false);
  };

  const handleYes = () => {
    hasToggled.current = true;
    updateField("permissionRelease", customField, true);
    updateField("permissionRelease", declinedField, false);
    if (showDeclinedSelected) {
      setShowDeclinedSelected(false);
      requestAnimationFrame(() => {
        setShowEditor(true);
      });
    } else {
      setShowEditor(true);
    }
  };

  const handleNo = () => {
    hasToggled.current = true;
    updateField("permissionRelease", customField, false);
    updateField("permissionRelease", declinedField, true);
    if (showDeclinedSelected) {
      nextSubStep();
    } else {
      setShowDeclinedSelected(true);
      setTimeout(() => nextSubStep(), 300);
    }
  };

  return (
    <Content $full>
      <div>
        <Title className="merri">{title}</Title>
        <Subtitle className="raleway">{subtitle}</Subtitle>
      </div>

      <FormCenter>
        <AnimatePresence mode="wait" onExitComplete={() => {
            if (pendingClear.current) {
              pendingClear.current = false;
              updateField("permissionRelease", textField, "");
            }
          }}>
          {!showEditor ? (
            <motion.div
              key="choices"
              initial={hasToggled.current ? "initial" : false}
              animate="enter"
              exit="exit"
            >
              <ChoiceRows>
                <motion.div variants={{
                  initial: { opacity: 0, y: -40 },
                  enter: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 150, damping: 15, delay: 0.08 } },
                  exit: { opacity: 0, y: -40, transition: { duration: 0.2, delay: 0 } },
                }}>
                  <ChoiceRow onClick={handleYes}>
                    <ChoiceRowInner>
                      <ChoiceIcon className={yesIcon} />
                      <ChoiceLabel>{yesLabel}</ChoiceLabel>
                    </ChoiceRowInner>
                  </ChoiceRow>
                </motion.div>
                <motion.div variants={{
                  initial: { opacity: 0, y: -40 },
                  enter: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 150, damping: 15, delay: 0 } },
                  exit: { opacity: 0, y: -40, transition: { duration: 0.2, delay: 0.08 } },
                }}>
                  <ChoiceRow onClick={handleNo} $selected={showDeclinedSelected}>
                    <ChoiceRowInner>
                      <ChoiceIcon className="fas fa-xmark" />
                      <ChoiceLabel>{noLabel}</ChoiceLabel>
                    </ChoiceRowInner>
                    <CheckIcon className="fas fa-circle-check" $visible={showDeclinedSelected} />
                  </ChoiceRow>
                </motion.div>
              </ChoiceRows>
            </motion.div>
          ) : (
            <motion.div
              key="editor"
              initial={hasToggled.current ? { opacity: 0, y: 40 } : false}
              animate={{ opacity: 1, y: 0, transition: { type: "spring", stiffness: 150, damping: 15 } }}
              exit={{ opacity: 0, y: 40, transition: { duration: 0.3 } }}
              style={{ display: "flex", flexDirection: "column", gap: 8 }}
            >
              <EditorField>
                <EditorLabel><i className="far fa-clipboard" /> Release Language</EditorLabel>
                <RichEditor
                  value={perm[textField]}
                  onChange={(html) =>
                    updateField("permissionRelease", textField, html)
                  }
                  placeholder={placeholder}
                  onCancel={handleCancel}
                />
              </EditorField>
            </motion.div>
          )}
        </AnimatePresence>
      </FormCenter>
    </Content>
  );
}

/* ─── Sub‑step screens ─── */

function GeneralRelease() {
  return (
    <ReleasePane
      title="General Release"
      subtitle="If you have a custom parental permission release and/or medical treatment release you can add it here and we'll have members agree to it when signing up."
      yesLabel="I have a custom parent release I'd like to add."
      noLabel="Don't add a permission slip here."
      customField="generalReleaseCustom"
      declinedField="generalReleaseDeclined"
      textField="generalReleaseText"
      placeholder="Enter your custom general release language..."
    />
  );
}

function PhotoRelease() {
  return (
    <ReleasePane
      title="Photo Release"
      subtitle="Do you have custom language for your photo release form?"
      yesIcon="fas fa-camera"
      yesLabel="I have a custom photo release I'd like to add."
      noLabel="Don't include a photo release."
      customField="photoReleaseCustom"
      declinedField="photoReleaseDeclined"
      textField="photoReleaseText"
      placeholder="Enter your custom photo release language..."
    />
  );
}

/* ─── Router ─── */

export default function PermissionRelease({ subStep }) {
  switch (subStep) {
    case 0: return <GeneralRelease />;
    case 1: return <PhotoRelease />;
    default: return <GeneralRelease />;
  }
}

/* ─── Styled components ─── */

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  ${(p) => p.$full && "height: 100%;"}
`;

const FormCenter = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 24px;
`;

const Title = styled.h2`
  font-size: 32px;
  margin: 0;
  color: #182941;
`;

const Subtitle = styled.p`
  font-size: 18px;
  color: #6b7280;
  margin: 4px 0 0 0;
`;

/* Choice cards */

const ChoiceRows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const ChoiceRow = styled.div`
  display: flex;
  align-items: center;
  padding: 22px 20px;
  background: ${(p) => (p.$selected ? "rgba(1, 96, 153, 0.04)" : "#fff")};
  border: 2px solid ${(p) => (p.$selected ? ACCENT_COLOR : "#e5e7eb")};
  border-radius: 12px;
  cursor: pointer;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  transition: border-color 0.2s, box-shadow 0.2s, background 0.15s;
  user-select: none;

  &:hover {
    border-color: ${ACCENT_COLOR};
    box-shadow: 0 3px 10px rgba(0, 0, 0, 0.12);
  }

  &:active {
    background: #f7f7f8;
  }

  &:active ${() => ChoiceRowInner} {
    transform: scale(0.97);
  }
`;

const ChoiceRowInner = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  transition: transform 0.15s;
`;

const ChoiceIcon = styled.i`
  font-size: 38px;
  flex-shrink: 0;
  color: ${ACCENT_COLOR};
`;

const ChoiceLabel = styled.div`
  font-size: 22px;
  color: #182941;
`;

const CheckIcon = styled.i`
  font-size: 38px;
  color: #00B44E;
  margin-left: auto;
  flex-shrink: 0;
  opacity: ${(p) => (p.$visible ? 1 : 0)};
  transition: opacity 0.2s;
`;

/* Rich editor */

const EditorField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  &:focus-within ${() => EditorLabel} {
    color: ${ACCENT_COLOR};
  }
`;

const EditorLabel = styled.label`
  font-size: 16px;
  color: #4b5563;
  padding-left: 16px;
  display: flex;
  align-items: center;
  gap: 6px;

  i {
    font-size: 13px;
  }
`;

const EditorWrap = styled.div`
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  transition: border-color 0.2s, box-shadow 0.2s;

  &:focus-within {
    border-color: ${ACCENT_COLOR};
    box-shadow: 0 3px 10px rgba(0, 0, 0, 0.12);
  }
`;

const Toolbar = styled.div`
  display: flex;
  gap: 2px;
  padding: 6px 6px;
  border-top: 1px solid #e5e7eb;
  background: #f9fafb;
`;

const ToolbarBtnGroup = styled.div`
  display: flex;
  gap: 2px;
  margin-left: auto;
`;

const ToolbarBtn = styled.button`
  appearance: none;
  border: none;
  background: none;
  cursor: pointer;
  padding: 6px 10px;
  border-radius: 6px;
  color: #374151;
  font-size: 13px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s, color 0.15s;

  > * {
    transition: transform 0.15s;
  }

  &:hover {
    background: #e5e7eb;
    color: ${ACCENT_COLOR};
  }

  &:active {
    background: #d1d5db;
    color: ${ACCENT_COLOR};

    > * {
      transform: scale(0.85);
    }
  }
`;

const CancelBtnInner = styled.span`
  display: flex;
  align-items: center;
  gap: 5px;
  transition: transform 0.15s;
`;

const CancelBtn = styled.button`
  appearance: none;
  border: none;
  background: none;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 6px;
  color: #6b7280;
  font-size: 12px;
  font-family: inherit;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  transition: background 0.15s, color 0.15s;

  &:hover {
    background: #e5e7eb;
    color: ${ACCENT_COLOR};
  }

  &:active {
    background: #d1d5db;
    color: ${ACCENT_COLOR};

    ${CancelBtnInner} {
      transform: scale(0.9);
    }
  }
`;

const Editor = styled.div`
  position: relative;
  padding: 14px 18px;
  min-height: 154px;
  font-size: 17px;
  background: #fff;
  font-family: inherit;
  outline: none;
  line-height: 1.6;

  &::before {
    content: ${(p) => (p.$empty ? "attr(data-placeholder)" : "none")};
    color: #9ca3af;
    pointer-events: none;
    position: absolute;
  }

  a {
    color: ${ACCENT_COLOR};
  }
`;

